/**
 * App shell — sections down the side, views across the top.
 *
 * Two levels, because the screens are two kinds of thing. Expenses (add,
 * journal, budget) are all answers about ONE MONTH and share a month bar.
 * Settings is not: your categories and the things you buy are not facts about
 * September, and putting them under a month selector said they were. So the
 * month bar belongs to the expenses section and appears nowhere else.
 *
 * Routing is hand-rolled — see the note above `Route` below.
 *
 * The identity picker is the DevAuthGuard stub made visible; it disappears
 * with the guard when Better Auth lands.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { BUCKETS } from '@et/core';
import type {
  Category,
  Entry,
  EntryList,
  Household,
  Merchant,
  PaymentMeans,
  PeriodSummary,
  Product,
} from '@et/contracts';
import {
  api,
  ApiError,
  authApi,
  loadIdentity,
  messageOf,
  saveIdentity,
  type DevIdentities,
  type Identity,
} from './api.js';
import { SignIn } from './SignIn.js';
import { NoHousehold } from './NoHousehold.js';
import { HouseholdSettings } from './Household.js';
import { Profile } from './Profile.js';
import { AccountMenu } from './AccountMenu.js';
import { clearPendingInvite, pendingInvite } from './invite.js';
import { QuickAdd } from './QuickAdd.js';
import { Shopping } from './Shopping.js';
import { EntryEditor } from './EntryEditor.js';
import {
  EntryFilters,
  isOverspent,
  Journal,
  remainingFigure,
  type JournalFilters,
} from './Journal.js';
import { MonthFilter } from './MonthFilter.js';
import { currentMonth, monthRange } from './months.js';
import { Budget, BudgetRail } from './Budget.js';
import {
  Categories,
  CategoryFilterBar,
  NO_CATEGORY_FILTERS,
  type CategoryFilters,
} from './Categories.js';
import {
  Analytics,
  AnalyticsFilterBar,
  DEFAULT_ANALYTICS_FILTERS,
  type AnalyticsFilters,
} from './Analytics.js';
import { PaymentMethods } from './PaymentMeans.js';
import { Merchants } from './Merchants.js';
import { SideNav, type Section } from './SideNav.js';

/**
 * Expenses is one page, not three tabs. The journal IS the section — it is what
 * you come to look at. Adding is an action, so it opens a modal; the budget is
 * a running commentary on what the journal shows, so it sits beside it in a
 * panel you can close when you would rather not be told.
 */
const BUDGET_OPEN_KEY = 'et.budgetPanel';

/** Wide enough for a journal and a budget panel to coexist without squeezing. */
const SIDE_BY_SIDE = '(min-width: 1200px)';

function initialBudgetOpen(): boolean {
  try {
    const stored = window.localStorage.getItem(BUDGET_OPEN_KEY);
    if (stored !== null) return stored === '1';
    // No preference yet: open where there is room, closed where it would cover
    // the thing it comments on.
    return window.matchMedia(SIDE_BY_SIDE).matches;
  } catch {
    return false;
  }
}

/** Every section names itself and says what it is for. */
/**
 * Routing, hand-rolled.
 *
 * Every navigable place gets a URL, which is what makes a screen linkable,
 * bookmarkable, reloadable and reachable with the back button — none of which
 * a `useState` section can do. This is fifty lines rather than a router
 * dependency because the whole map is five paths deep by one, there are no
 * route params, no nested layouts and no data loaders; a library here would be
 * more configuration than the thing it configures.
 *
 * If that stops being true — a category detail page, an entry permalink,
 * anything with an id in the path — swap this for a real router rather than
 * growing it.
 *
 * NOTE: deep links need the dev server to serve index.html for unknown paths.
 * Vite does that by default for an SPA; a production host must be told to.
 */
type Route =
  | { section: 'expenses' }
  | { section: 'shopping' }
  | { section: 'analytics' }
  | { section: 'household' }
  | { section: 'profile' }
  | { section: 'settings'; tab: SettingsTab };

/** The URL segment per settings tab. "payment-methods" reads better than the
 *  internal 'payment', and a URL is a thing people see. */
const SETTINGS_SEGMENT: Record<SettingsTab, string> = {
  categories: 'categories',
  merchants: 'merchants',
  payment: 'payment-methods',
};

const HOME: Route = { section: 'expenses' };

function pathOf(route: Route): string {
  return route.section === 'settings'
    ? `/settings/${SETTINGS_SEGMENT[route.tab]}`
    : `/${route.section}`;
}

/** Null for anything unrecognised, so the caller decides what to do about it. */
function parseRoute(pathname: string): Route | null {
  const [head, tail] = pathname.split('/').filter(Boolean);
  if (head === 'expenses') return { section: 'expenses' };
  if (head === 'shopping') return { section: 'shopping' };
  if (head === 'analytics') return { section: 'analytics' };
  if (head === 'household') return { section: 'household' };
  if (head === 'profile') return { section: 'profile' };
  if (head === 'settings') {
    const tab = (Object.keys(SETTINGS_SEGMENT) as SettingsTab[]).find(
      (t) => SETTINGS_SEGMENT[t] === tail,
    );
    // /settings alone lands on the first tab rather than 404ing.
    return { section: 'settings', tab: tab ?? 'categories' };
  }
  return null;
}

const PAGE_TITLE: Record<Section, string> = {
  expenses: 'Expenses',
  shopping: 'Shopping',
  analytics: 'Analytics',
  household: 'Household',
  profile: 'Your profile',
  settings: 'Settings',
};

/** Settings has no blurb of its own — each tab supplies one. */
const PAGE_BLURB: Record<Section, string> = {
  expenses: 'Record what you spent, look back over it, and check it against the month\u2019s plan.',
  shopping:
    'What you mean to buy. Tick things off as you shop, then turn what you got into an expense.',
  analytics:
    'What the ledger adds up to. Pick a range, narrow it if you want, and read what actually happened.',
  household:
    'Who is in this household and what they may do. Everyone here shares the categories, the budget and the journal \u2014 except whatever is marked private.',
  profile:
    'Your name and picture, as they follow you into every household. What one household calls you is set there, not here.',
  settings: '',
};

/**
 * The lists the app is built on. They are one section because they are one
 * kind of thing — set up once, then rarely touched — and because none of them
 * is scoped to a month, which is the distinction the left rail exists to make.
 * Each keeps its own sentence: "Settings" says where you are, not what the
 * screen in front of you does.
 */
type SettingsTab = 'categories' | 'merchants' | 'payment';

const SETTINGS_TABS: Array<{ id: SettingsTab; label: string; blurb: string }> = [
  {
    id: 'categories',
    label: 'Categories & items',
    blurb:
      'Each category, the bucket it feeds, and the things you buy under it. Changing a bucket moves money between Needs, Wants and Savings.',
  },
  {
    id: 'merchants',
    label: 'Merchants',
    blurb:
      'Where your money goes. The list fills itself in from what you record; rename one and every entry that used it follows.',
  },
  {
    id: 'payment',
    label: 'Money & payment',
    blurb:
      'The currency everything is counted in, and the ways you pay. Rename a payment method and every entry that used it follows.',
  },
];

export function App() {
  const [identities, setIdentities] = useState<DevIdentities | null>(null);
  /**
   * How identity is established here, and who is signed in.
   *
   * `undefined` is a third state the UI needs, not laziness: it means "not
   * asked yet", and rendering the sign-in screen before the answer arrives
   * would flash a login form at somebody who is already signed in.
   */
  const [signIn, setSignIn] = useState<
    { mode: 'session' | 'dev-headers'; google: boolean } | undefined
  >(undefined);
  const [account, setAccount] = useState<{ id: string; name: string; email: string } | null>(null);
  /** Signed in, but the API says this account has no seat anywhere yet. */
  const [noHousehold, setNoHousehold] = useState(false);
  /**
   * Whether the rail is collapsed to icons.
   *
   * Remembered per browser, not per account: it is a preference about this
   * screen's width, and someone on a laptop and a big monitor reasonably wants
   * different answers. `localStorage` read lazily so the first paint is already
   * in the right state — reading it in an effect would flash the wide rail.
   */
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('et.nav') === 'collapsed';
    } catch {
      return false;
    }
  });
  /** Every household this account can act in. Drives the switcher. */
  /** The account's own name and face, for the avatar. Separate from `account`,
   *  which is what the session said at sign-in and does not change when the
   *  profile does. */
  const [me, setMe] = useState<{ name: string; email: string; image: string | null; avatar: string | null } | null>(null);
  const [households, setHouseholds] = useState<
    Array<{ householdId: string; name: string; memberId: string; role: string }>
  >([]);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [household, setHousehold] = useState<Household | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [entries, setEntries] = useState<EntryList | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [means, setMeans] = useState<PaymentMeans[]>([]);

  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname) ?? HOME);
  const section = route.section;
  // Settings' body reads this even when another section is showing; the tab it
  // returns to is whichever the URL last named.
  const settingsTab: SettingsTab = route.section === 'settings' ? route.tab : 'categories';

  const go = useCallback((next: Route) => {
    // Guarded, so clicking the section you are already on does not stack a
    // history entry that the back button then has to chew through.
    if (window.location.pathname !== pathOf(next)) {
      window.history.pushState(null, '', pathOf(next));
    }
    setRoute(next);
  }, []);

  // "/" and anything unrecognised become the real path, without a history
  // entry — landing on a typo should not give you a back button to the typo.
  // A path that parses but is not its own canonical form is normalised too:
  // /settings/items was a real URL until the Items screen was folded into
  // Categories, and a bookmark should heal rather than sit there lying.
  useEffect(() => {
    const parsed = parseRoute(window.location.pathname);
    const canonical = pathOf(parsed ?? HOME);
    if (window.location.pathname !== canonical) {
      window.history.replaceState(null, '', canonical);
    }
  }, []);

  useEffect(() => {
    function onPop() {
      setRoute(parseRoute(window.location.pathname) ?? HOME);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  /** The Categories tab's own search and facets — same split as the journal's. */
  const [catQ, setCatQ] = useState('');
  const [catFilters, setCatFilters] = useState<CategoryFilters>(NO_CATEGORY_FILTERS);
  /** Analytics' own range and facets, held here so the section keeps them across visits. */
  const [anaFilters, setAnaFilters] = useState<AnalyticsFilters>(DEFAULT_ANALYTICS_FILTERS);
  /**
   * Lines handed over by finishing a shop. Held here rather than inside the
   * modal because the modal is unmounted when closed — state that must survive
   * the click that opens it cannot live inside it.
   */
  const [draftLines, setDraftLines] = useState<Array<{ name: string; quantity: string }> | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(initialBudgetOpen);
  const [filters, setFilters] = useState<JournalFilters>({
    month: currentMonth(),
    spentBy: '',
    bucket: '',
    paymentMeansId: '',
  });

  /**
   * The header's search text. Deliberately beside `filters` rather than in it:
   * `loadEntries` depends on that object, so a letter typed here would refetch
   * the whole month (AGENTS.md §3.5e).
   */
  const [q, setQ] = useState('');

  /**
   * Whether the journal's headline has scrolled behind the header, in which
   * case the header carries the figure instead. Journal reports it, because
   * Journal owns the element; `setSummaryHidden` is a stable setter, so the
   * listener over there is not rebuilt on every render.
   */
  const [summaryHidden, setSummaryHidden] = useState(false);

  const [loading, setLoading] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [budget, setBudget] = useState<PeriodSummary | null>(null);

  // --- an invite link -------------------------------------------------------
  //
  // `/join/<token>` is handled here rather than as a route, because it is not a
  // place — it is an action that happens once and then never again. Redeeming
  // it needs a session, so somebody arriving cold signs in or signs up first
  // and the token waits in sessionStorage across that round trip.
  useEffect(() => {
    if (!account) return;
    const token = pendingInvite();
    if (!token) return;

    void authApi
      .acceptInvite(token)
      .then((result) => {
        clearPendingInvite();
        if (!result.ok && result.message) setFatal(result.message);
        // Land in the household that was just joined. A reload is the honest
        // way: the seat is new, so every list on screen is for the wrong one.
        else window.location.assign('/');
      })
      .catch(() => clearPendingInvite());
  }, [account]);

  // --- boot: how does identity work here, and do we have one? ---------------
  //
  // The SERVER is asked rather than assumed. In session mode a cookie decides;
  // with AUTH_MODE=dev-headers the old picker still runs; and the browser has
  // no way to tell which. Guessing wrong either shows a sign-in screen that
  // could never work, or hides the only way in.
  useEffect(() => {
    let cancelled = false;
    authApi
      .options()
      .then(async (options) => {
        if (cancelled) return;
        setSignIn(options);
        if (options.mode === 'session') {
          const current = await authApi.session();
          if (!cancelled) setAccount(current?.user ?? null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setFatal(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The switcher's list. Account-scoped, so it survives switching and does not
  // need reloading when the current household changes.
  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    void authApi
      .profile()
      .then((p) => setMe(p))
      .catch(() => {
        /* the avatar falls back to the session's name; not worth an error */
      });

    authApi
      .myHouseholds()
      .then((r) => {
        if (!cancelled) setHouseholds(r.households);
      })
      .catch(() => {
        /* the switcher is not worth an error over; the app still works */
      });
    return () => {
      cancelled = true;
    };
  }, [account, noHousehold]);

  // --- boot: pick an identity -----------------------------------------------
  useEffect(() => {
    let cancelled = false;

    if (signIn === undefined) return;

    // --- session mode: the cookie is the identity ---------------------------
    // Nothing to pick. Ask the API who we are and where, and let its answer —
    // including which member we are — be the identity. `identities()` is the
    // dev picker and has no business running here.
    if (signIn.mode === 'session') {
      if (!account) return;
      const hint = loadIdentity();
      api
        .household(hint ?? { householdId: '', memberId: '' })
        .then((h) => {
          if (cancelled) return;
          setNoHousehold(false);
          setIdentity({ householdId: h.id, memberId: h.meMemberId });
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.code === 'NO_HOUSEHOLD') setNoHousehold(true);
          else setFatal(messageOf(e));
        });
      return () => {
        cancelled = true;
      };
    }

    // --- dev-headers mode: the old picker -----------------------------------
    api
      .identities()
      .then((data) => {
        if (cancelled) return;
        setIdentities(data);

        const remembered = loadIdentity();
        const stillValid =
          remembered &&
          data.households.some(
            (h) =>
              h.householdId === remembered.householdId &&
              h.members.some((m) => m.memberId === remembered.memberId),
          );

        if (stillValid && remembered) {
          setIdentity(remembered);
          return;
        }

        const first = data.households[0];
        const member = first?.members[0];
        if (first && member) {
          setIdentity({ householdId: first.householdId, memberId: member.memberId });
        } else {
          setFatal('No households found. Has the database been seeded?');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // NO_HOUSEHOLD is not a failure, it is a state with its own screen.
        if (e instanceof ApiError && e.code === 'NO_HOUSEHOLD') setNoHousehold(true);
        else setFatal(messageOf(e));
      });

    return () => {
      cancelled = true;
    };
  }, [signIn, account]);

  useEffect(() => {
    if (identity) saveIdentity(identity);
  }, [identity]);

  /**
   * Paint the household's chosen bucket colours onto `:root`.
   *
   * Only the ones actually chosen. An inline custom property beats the
   * stylesheet, so setting one is enough — but REMOVING it is what puts a
   * bucket back, because the stylesheet carries a light AND a dark value per
   * bucket and a single stored hex cannot. Writing a resolved default here
   * would freeze today's palette into the page and quietly break dark mode.
   *
   * On `documentElement` rather than a wrapper: the dots are everywhere, and
   * anything portalled or fixed still has to see them.
   */
  useEffect(() => {
    const root = document.documentElement;
    const chosen = household?.bucketColors;
    for (const bucket of BUCKETS) {
      const value = chosen?.[bucket] ?? null;
      if (value) root.style.setProperty(`--${bucket.toLowerCase()}`, value);
      else root.style.removeProperty(`--${bucket.toLowerCase()}`);
    }
  }, [household]);

  useEffect(() => {
    try {
      window.localStorage.setItem(BUDGET_OPEN_KEY, budgetOpen ? '1' : '0');
    } catch {
      /* a remembered panel state is a convenience, never worth an error */
    }
  }, [budgetOpen]);

  /**
   * Publish the header's real height so anything that sticks below it can do
   * the arithmetic. It is not a constant: the header wraps on medium widths and
   * loses its blurb on small ones, and a hard-coded offset would tuck the budget
   * panel underneath at exactly the widths nobody tests.
   *
   * A CALLBACK ref, not `useRef` + `useEffect([])`. This component returns early
   * while it is still booting, so a mount effect registers during the boot
   * screen — when the header does not exist — reads null, and with an empty
   * dependency array never runs again. A callback ref fires when the node
   * actually attaches, which is the thing being waited for.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const headRef = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;

    const apply = () =>
      document.documentElement.style.setProperty('--head-h', `${node.offsetHeight}px`);

    apply();
    observer.current = new ResizeObserver(apply);
    observer.current.observe(node);
  }, []);

  // A modal you cannot dismiss with the key everyone reaches for is a trap.
  useEffect(() => {
    if (!addOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAddOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addOpen]);

  // --- household + categories change only when identity does ----------------
  useEffect(() => {
    if (!identity) return;
    let cancelled = false;

    Promise.all([
      api.household(identity),
      api.categories(identity),
      api.products(identity, { limit: 500 }),
      api.merchants(identity),
      api.paymentMeans(identity),
    ])
      .then(([h, cats, catalogue, places, payment]) => {
        if (cancelled) return;
        setHousehold(h);
        setCategories(cats);
        setProducts(catalogue.products);
        setMerchants(places.merchants);
        setMeans(payment);
        setFatal(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setFatal(messageOf(e));
      });

    return () => {
      cancelled = true;
    };
  }, [identity]);

  // --- entries follow the filters -------------------------------------------
  const loadEntries = useCallback(async () => {
    if (!identity) return;
    setLoading(true);
    try {
      const { from, to } = monthRange(filters.month);
      const data = await api.entries(identity, {
        from,
        to,
        ...(filters.spentBy ? { spentBy: filters.spentBy } : {}),
        ...(filters.bucket ? { bucket: filters.bucket as 'NEEDS' | 'WANTS' | 'SAVINGS' } : {}),
        ...(filters.paymentMeansId ? { paymentMeansId: filters.paymentMeansId } : {}),
      });
      setEntries(data);
      setFatal(null);
    } catch (e: unknown) {
      setFatal(messageOf(e));
    } finally {
      setLoading(false);
    }
  }, [identity, filters]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  // The budget follows whichever month the journal is showing, so switching
  // month once moves both screens.
  const loadBudget = useCallback(async () => {
    if (!identity) return;
    try {
      setBudget(await api.budget(identity, filters.month));
    } catch (e: unknown) {
      setFatal(messageOf(e));
    }
  }, [identity, filters.month]);

  useEffect(() => {
    void loadBudget();
  }, [loadBudget]);

  // Adding an expense should also refresh the category ranking that put its
  // chip near the front — otherwise the ordering lags a day behind reality.
  const afterSave = useCallback(() => {
    void loadEntries();
    void loadBudget();
    if (identity) {
      api
        .categories(identity)
        .then(setCategories)
        .catch(() => {
          /* ranking is a nicety; a failure here is not worth interrupting anyone */
        });
      // An item typed on a line exists from now on — the type-ahead should
      // know about it on the very next entry, not after a reload.
      api
        .products(identity, { limit: 500 })
        .then((catalogue) => setProducts(catalogue.products))
        .catch(() => {
          /* likewise */
        });
      api
        .merchants(identity)
        .then((places) => setMerchants(places.merchants))
        .catch(() => {
          /* likewise */
        });
    }
  }, [identity, loadEntries, loadBudget]);

  const handleDelete = useCallback(
    async (target: Entry) => {
      if (!identity) return;
      const label = target.merchantName ?? target.note ?? 'this entry';
      const currency = household?.baseCurrency ?? '';
      if (!window.confirm(`Delete ${label} — ${target.amount} ${currency}?`.trim())) return;
      try {
        await api.deleteEntry(identity, target.id);
        afterSave();
      } catch (e: unknown) {
        setFatal(messageOf(e));
      }
    },
    // `household` belongs here: without it the callback keeps whatever value
    // household had when this list last changed, which is null on first render.
    [identity, household, afterSave],
  );

  // Before there is an identity there is no navigation to draw, so these two
  // deliberately skip the shell rather than render an empty one.
  if (fatal && !household) {
    return (
      <main className="boot">
        <h1>Excent</h1>
        <p className="error">{fatal}</p>
        <p className="hint">
          Check the stack is up: <code>docker compose up</code>, then reload.
        </p>
      </main>
    );
  }

  // Signed in, but with no household — the state a brand-new account is in.
  // It is a SCREEN, not an error: the person who hits it first is usually the
  // one who set this up, and telling them to go ask somebody was a dead end
  // with no button under it.
  if (signIn?.mode === 'session' && account && noHousehold) {
    return (
      <NoHousehold
        onReady={() => {
          // The household exists now; re-running the boot effect is what picks
          // it up, and the cleanest way to be sure nothing stale survives is to
          // start from a clean slate.
          window.location.assign('/');
        }}
        onSignOut={() => void signOut()}
      />
    );
  }

  // The door. Before anything else, because everything else assumes a session.
  if (signIn?.mode === 'session' && !account) {
    return (
      <SignIn
        google={signIn.google}
        onSignedIn={() => {
          // Re-ask rather than trusting what the form returned: the session is
          // a cookie now, and the only thing that proves it landed is reading
          // it back. Setting `account` is also what releases the boot effect
          // that loads the household.
          void authApi.session().then((current) => setAccount(current?.user ?? null));
        }}
      />
    );
  }

  if (!identity || !household) {
    return (
      <main className="boot">
        <h1>Excent</h1>
        <p className="hint">Loading…</p>
      </main>
    );
  }

  /**
   * Sign out, then forget everything the browser was holding.
   *
   * The reload is not laziness — it is the only way to be sure. This app keeps
   * a household, its categories, its merchants and a month of entries in React
   * state; clearing them one by one is a list that grows every time a screen is
   * added, and the day somebody forgets an entry is the day one person's
   * spending is briefly visible to the next person who signs in on this laptop.
   */
  /** Re-read the household after its people or its name changed. */
  const reloadHousehold = async () => {
    try {
      setHousehold(await api.household(identity));
    } catch (e: unknown) {
      setFatal(messageOf(e));
    }
  };

  const signOut = async () => {
    try {
      await authApi.signOut();
    } finally {
      window.location.assign('/');
    }
  };

  const role = household.members.find((m) => m.id === identity.memberId)?.role;

  return (
    <div className="shell">
      <SideNav
        section={section}
        onSection={(next) => go(next === 'settings' ? { section: 'settings', tab: settingsTab } : { section: next })}
        hrefOf={(next) => pathOf(next === 'settings' ? { section: 'settings', tab: settingsTab } : { section: next })}
        collapsed={navCollapsed}
        onToggleCollapsed={() =>
          setNavCollapsed((current) => {
            const next = !current;
            try {
              localStorage.setItem('et.nav', next ? 'collapsed' : 'open');
            } catch {
              /* private mode: the toggle still works, it just will not persist */
            }
            return next;
          })
        }
        identity={identity}
        identities={identities}
        onIdentity={setIdentity}
      />

      <main className="content">
        {/* Every page names itself. The month filter appears in this header and
            nowhere else, because Expenses is the only section scoped by one —
            a category tree is not a fact about September. */}
        {/* A band, not a block: it spans the content column edge to edge and
            carries its own surface, so the page reads as chrome-then-content
            rather than as one undifferentiated sheet. The inner wrapper keeps
            its contents on the same width as the screen below. */}
        <header className="page-head" ref={headRef}>
          <div className="page-head-inner">
          <div className="page-head-text">
            <h1>{PAGE_TITLE[section]}</h1>
            <p>
              {section === 'settings'
                ? SETTINGS_TABS.find((t) => t.id === settingsTab)?.blurb
                : PAGE_BLURB[section]}
            </p>
          </div>

          {section === 'expenses' && (
            <div className="page-head-tools">
              <MonthFilter
                month={filters.month}
                onChange={(month) => setFilters((f) => ({ ...f, month }))}
              />
              <div className="page-actions">
                {/* On a phone this becomes a floating button above the nav bar —
                    the top of a tall screen is the one place a thumb cannot go. */}
                <button type="button" className="primary add-button" onClick={() => setAddOpen(true)}>
                  <span aria-hidden="true">+</span> Add
                </button>
              </div>
            </div>
          )}

          {/* Top right, on every page. Identity belongs in one fixed place. */}
          {account && (
            <AccountMenu
              account={{ name: me?.name ?? account.name, email: account.email }}
              avatar={me?.avatar ?? null}
              image={me?.image ?? null}
              currentHouseholdId={identity.householdId}
              households={households}
              onSwitch={(to) => {
                saveIdentity(to);
                window.location.assign('/');
              }}
              onGo={(to) => go({ section: to })}
              onSignOut={() => void signOut()}
            />
          )}
          </div>

          {/* The filter belongs to this band, not to a strip under it: the
              header is the one thing already sticky, so search stays reachable
              twenty entries down without a second pinned row of its own. */}
          {section === 'analytics' && (
            <div className="page-head-filters">
              <AnalyticsFilterBar
                categories={categories}
                filters={anaFilters}
                onFiltersChange={setAnaFilters}
              />
            </div>
          )}

          {section === 'settings' && (
            <div className="page-head-filters">
              {/* The same segmented control the journal's views use, rather
                  than a second switcher shape to learn. Group and
                  aria-pressed, not tablist/tab: real tabs owe the reader
                  aria-controls and a tabpanel, and half-built ARIA is worse
                  than none. */}
              <div className="settings-head-row">
                <div className="type-switch" role="group" aria-label="Settings sections">
                  {SETTINGS_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={settingsTab === tab.id ? 'on' : ''}
                      aria-pressed={settingsTab === tab.id}
                      onClick={() => go({ section: 'settings', tab: tab.id })}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Only Categories has these — a list of four payment methods
                    has nothing to narrow. The search matches an item's name as
                    well as a category's, so "eggs" finds the category it sits
                    under. */}
                {settingsTab === 'categories' && (
                  <CategoryFilterBar
                    q={catQ}
                    onQChange={setCatQ}
                    filters={catFilters}
                    onFiltersChange={setCatFilters}
                    archivedCount={
                      categories.filter((c) => c.archived && c.bucket !== 'INCOME').length
                    }
                  />
                )}
              </div>
            </div>
          )}

          {section === 'expenses' && (
            <div className="page-head-filters">
              <EntryFilters
                filters={filters}
                onFiltersChange={setFilters}
                q={q}
                onQChange={setQ}
                members={household.members}
                means={means}
                figure={remainingFigure(budget, household.baseCurrency)}
                showFigure={summaryHidden}
                overspent={isOverspent(budget)}
              />
            </div>
          )}
        </header>

        {fatal && <p className="error">{fatal}</p>}

        {section === 'analytics' && (
          <div className="screen">
            <Analytics identity={identity} filters={anaFilters} />
          </div>
        )}

        {section === 'shopping' && (
          <div className="screen">
            <Shopping
              identity={identity}
              products={products}
              canEdit={role !== 'VIEWER'}
              onShopDone={(_list, bought) => {
                // The list is not emptied and nothing is recorded yet: the Add
                // screen is a draft until it is confirmed, and a shop that is
                // abandoned halfway must leave the list exactly as it was.
                setDraftLines(bought.map((i) => ({ name: i.name, quantity: i.quantity })));
                go(HOME);
                setAddOpen(true);
              }}
            />
          </div>
        )}

        {section === 'expenses' && (
          <div className={budgetOpen ? 'expenses with-panel' : 'expenses'}>
            <div className="screen">
              <Journal
                household={household}
                data={entries}
                summary={budget}
                filters={filters}
                q={q}
                onSummaryHidden={setSummaryHidden}
                onEdit={setEditing}
                onDelete={handleDelete}
                loading={loading}
              />
            </div>

            {!budgetOpen && (
              <div className="budget-slot budget-slot-rail">
                <BudgetRail summary={budget} onOpen={() => setBudgetOpen(true)} />
              </div>
            )}

            {/* The slot is absolutely positioned, so opening the budget cannot
                make this row taller than the journal it sits beside — see the
                CSS. The panel inside it still sticks. */}
            {budgetOpen && (
              <div className="budget-slot">
              <aside className="budget-panel" aria-label="Budget for this month">
                <div className="budget-panel-head">
                  <h2>Budget</h2>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setBudgetOpen(false)}
                    aria-label="Hide budget"
                  >
                    ×
                  </button>
                </div>
                <Budget
                  identity={identity}
                  summary={budget}
                  month={filters.month}
                  canEditProfile={role === 'OWNER'}
                  onProfileSaved={loadBudget}
                  loading={loading}
                />
              </aside>
              </div>
            )}
          </div>
        )}

        {section === 'profile' && (
          <div className="screen">
            <Profile
              onChanged={() => {
                void authApi.profile().then((p) => setMe(p));
              }}
            />
          </div>
        )}

        {section === 'household' && (
          <div className="screen">
            <HouseholdSettings
              identity={identity}
              household={household}
              canEdit={role === 'OWNER'}
              onChanged={() => void reloadHousehold()}
            />
          </div>
        )}

        {section === 'settings' && (
          <div className="screen">
            {settingsTab === 'categories' && (
              <Categories
                identity={identity}
                categories={categories}
                products={products}
                bucketColors={household.bucketColors}
                onHouseholdChanged={setHousehold}
                canEdit={role === 'OWNER'}
                canEditItems={role !== 'VIEWER'}
                q={catQ}
                filters={catFilters}
                onChanged={(next) => {
                  setCategories(next);
                  // A re-mapped category moves money between buckets, so the
                  // budget numbers are stale the moment this returns.
                  void loadBudget();
                }}
                onProductsChanged={() => {
                  // The catalogue is what the Add screen reads from, so it is
                  // not enough for this screen to know.
                  api
                    .products(identity, { limit: 500 })
                    .then((catalogue) => setProducts(catalogue.products))
                    .catch(() => {
                      /* the screen has already refreshed what it shows */
                    });
                }}
              />
            )}

            {settingsTab === 'merchants' && (
              <Merchants
                identity={identity}
                merchants={merchants}
                canEdit={role !== 'VIEWER'}
                onChanged={setMerchants}
              />
            )}

            {settingsTab === 'payment' && (
              <PaymentMethods
                identity={identity}
                means={means}
                household={household}
                canEdit={role === 'OWNER'}
                onChanged={setMeans}
                onHouseholdChanged={setHousehold}
              />
            )}

          </div>
        )}

      </main>
      {addOpen && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Add an entry"
          onMouseDown={(event) => {
            // Only a click that both starts and ends on the backdrop closes it,
            // so a text selection dragged out of the form does not.
            if (event.target === event.currentTarget) setAddOpen(false);
          }}
        >
          <div className="modal modal-wide">
            <div className="modal-head">
              <h2>Add</h2>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setAddOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <QuickAdd
                identity={identity}
                household={household}
                categories={categories}
                month={filters.month}
                products={products}
                merchants={merchants}
                means={means}
                {...(draftLines ? { draftLines } : {})}
                onSaved={() => {
                  afterSave();
                  // Closing is the confirmation: the entry appears in the
                  // journal behind, which says more than a toast would.
                  setAddOpen(false);
                  setDraftLines(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EntryEditor
          identity={identity}
          household={household}
          categories={categories}
          entry={editing}
          merchants={merchants}
          means={means}
          onClose={() => setEditing(null)}
          onSaved={afterSave}
        />
      )}
    </div>
  );
}
